import { AuthService } from '../../auth/authService'
import { getEnvSettings } from '../configurationServices'
import { getSecureToken, getEnterpriseIdFromToken } from '../get-secure-token'
import { 
  Assignment,
  AssignmentError, 
  AgencyDisplayResponse, 
  UpsertAgencyDisplaysRequest, 
  UpsertAgencyDisplaysResponse,
  ScreenDisplayDetailResponse,
  UpsertScreenRequest,
  UpsertScreenResponse
} from './types'

const { NOC_DISPLAY_BASE_URL } = getEnvSettings()


/**
 * Fetches the list of displays for the agencies
 * @returns Promise with the list of agency displays
 */
export const fetchAgencyDisplayAssignments = async (filters: any = {}, enterpriseId?: string): Promise<AgencyDisplayResponse> => {
  const tokenData = await getSecureToken()
  const token = tokenData.access_token

  if (!token) {
    throw new AssignmentError('Token no encontrado', 'AUTH_ERROR')
  }

  const tokenEnterpriseId = getEnterpriseIdFromToken()
  const effectiveEnterpriseId = enterpriseId || tokenEnterpriseId

  const response = await fetch(`${NOC_DISPLAY_BASE_URL}/agency-displays`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(effectiveEnterpriseId && { 'X-Placebet-Enterprise': effectiveEnterpriseId })
    },
    body: JSON.stringify(filters),
  })

  let responseData: AgencyDisplayResponse

  try {
    responseData = await response.json()
  } catch {
    throw new AssignmentError(
      `No se pudo procesar la respuesta del servidor: ${response.status} ${response.statusText}`,
      response.ok ? 'UNKNOWN' : `${response.status}`,
    )
  }

  if (!response.ok) {
    throw new AssignmentError(
      `Error al obtener los agency displays (${response.status})`,
      `${response.status}`,
    )
  }

  if (!responseData.success || !Array.isArray(responseData.data)) {
    throw new AssignmentError('Respuesta del servidor inválida', 'INVALID_RESPONSE')
  }

  return responseData
}

/**
 * Upserts (creates or updates) agency displays
 * @param payload - The upsert request body
 * @returns Promise with the upsert response
 */
export const upsertAgencyDisplays = async (
  payload: UpsertAgencyDisplaysRequest,
  enterpriseId?: string
): Promise<UpsertAgencyDisplaysResponse> => {
  const tokenData = await getSecureToken()
  const token = tokenData.access_token

  if (!token) {
    throw new AssignmentError('Token no encontrado', 'AUTH_ERROR')
  }

  const tokenEnterpriseId = getEnterpriseIdFromToken()
  const effectiveEnterpriseId = enterpriseId || tokenEnterpriseId

  const response = await fetch(`${NOC_DISPLAY_BASE_URL}/agency-displays/upsert`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(effectiveEnterpriseId && { 'X-Placebet-Enterprise': effectiveEnterpriseId })
    },
    body: JSON.stringify({
      ...payload,
      enterprise_id: payload.enterprise_id || effectiveEnterpriseId,
    }),
  })

  let responseData: UpsertAgencyDisplaysResponse

  try {
    responseData = await response.json()
  } catch {
    throw new AssignmentError(
      `No se pudo procesar la respuesta del servidor: ${response.status} ${response.statusText}`,
      response.ok ? 'UNKNOWN' : `${response.status}`,
    )
  }

  if (!response.ok) {
    throw new AssignmentError(
      `Error al guardar los cambios (${response.status})`,
      `${response.status}`,
    )
  }

  return responseData
}
/**
 * Fetches the details for the screen display
 * @returns Promise with the screen display detail
 */
export const fetchScreenDisplayDetail = async (enterpriseId?: string): Promise<ScreenDisplayDetailResponse> => {
  const tokenData = await getSecureToken()
  const token = tokenData.access_token

  if (!token) {
    throw new AssignmentError('Token no encontrado', 'AUTH_ERROR')
  }

  const tokenEnterpriseId = getEnterpriseIdFromToken()
  const effectiveEnterpriseId = enterpriseId || tokenEnterpriseId

  const response = await fetch(`${NOC_DISPLAY_BASE_URL}/screen-administration-data`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(effectiveEnterpriseId && { 'X-Placebet-Enterprise': effectiveEnterpriseId })
    },
    body: JSON.stringify({}),
  })

  let responseData: ScreenDisplayDetailResponse

  try {
    responseData = await response.json()
  } catch {
    throw new AssignmentError(
      `No se pudo procesar la respuesta del servidor: ${response.status} ${response.statusText}`,
      response.ok ? 'UNKNOWN' : `${response.status}`,
    )
  }

  if (!response.ok) {
    throw new AssignmentError(
      `Error al obtener los detalles del screen display (${response.status})`,
      `${response.status}`,
    )
  }

  if (!responseData.success || !responseData.data) {
    throw new AssignmentError('Respuesta del servidor inválida', 'INVALID_RESPONSE')
  }

  return responseData
}

/**
 * Upserts (creates or updates) a screen
 * @param payload - The upsert screen request body
 * @param enterpriseId - Optional enterprise ID override
 * @returns Promise with the upsert response
 */
export const upsertScreen = async (
  payload: UpsertScreenRequest,
  enterpriseId?: string
): Promise<UpsertScreenResponse> => {
  const tokenData = await getSecureToken()
  const token = tokenData.access_token

  if (!token) {
    throw new AssignmentError('Token no encontrado', 'AUTH_ERROR')
  }

  const tokenEnterpriseId = getEnterpriseIdFromToken()
  const effectiveEnterpriseId = enterpriseId || payload.enterprise_id || tokenEnterpriseId

  const response = await fetch(`${NOC_DISPLAY_BASE_URL}/screens/upsert`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(effectiveEnterpriseId && { 'X-Placebet-Enterprise': effectiveEnterpriseId })
    },
    body: JSON.stringify({
      ...payload,
      enterprise_id: effectiveEnterpriseId,
    }),
  })

  let responseData: UpsertScreenResponse

  try {
    responseData = await response.json()
  } catch {
    throw new AssignmentError(
      `No se pudo procesar la respuesta del servidor: ${response.status} ${response.statusText}`,
      response.ok ? 'UNKNOWN' : `${response.status}`,
    )
  }

  if (!response.ok) {
    throw new AssignmentError(
      `Error al procesar el screen (${response.status})`,
      `${response.status}`,
    )
  }

  return responseData
}

/**
 * Synchronizes assignments with the backend
 * @param enterpriseId - Optional enterprise ID override
 * @returns Promise with the sync response data
 */
export const syncAssignments = async (enterpriseId?: string): Promise<{ success: boolean; data: Assignment[]; message?: string }> => {
  const tokenData = await getSecureToken()
  const token = tokenData.access_token
  const authService = new AuthService()
  const user = await authService.getUser()
  
  if (!token) {
    throw new AssignmentError('Token no encontrado', 'AUTH_ERROR')
  }

  const tokenEnterpriseId = getEnterpriseIdFromToken()
  const effectiveEnterpriseId = enterpriseId || tokenEnterpriseId

  const response = await fetch(`${NOC_DISPLAY_BASE_URL}/assignments/sync`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-admin-user': user?.profile.preferred_username || '',
      ...(effectiveEnterpriseId && { 'X-Placebet-Enterprise': effectiveEnterpriseId })
    },
    body: JSON.stringify({}),
  })

  let responseData: any

  try {
    responseData = await response.json()
  } catch {
    throw new AssignmentError(
      `No se pudo procesar la respuesta del servidor: ${response.status} ${response.statusText}`,
      response.ok ? 'UNKNOWN' : `${response.status}`,
    )
  }

  if (!response.ok) {
    throw new AssignmentError(
      `Error al sincronizar assignments (${response.status})`,
      `${response.status}`,
    )
  }

  return {
    success: true,
    data: responseData.data,
    message: responseData.message || 'Sincronización completada'
  }
}

/**
 * Deletes the fingerprint of an agency server (Disassociates the server from the agent)
 * @param serverId - The ID of the server
 * @param licenseKey - The license key of the server
 * @param enterpriseId - Optional enterprise ID override
 * @returns Promise with the result detail
 */
export const deleteServerFingerprint = async (
  serverId: string,
  licenseKey: string,
  enterpriseId?: string
): Promise<{ success: boolean; detail: string }> => {
  const tokenData = await getSecureToken()
  const token = tokenData.access_token
  const authService = new AuthService()
  const user = await authService.getUser()

  if (!token) {
    throw new AssignmentError('Token no encontrado', 'AUTH_ERROR')
  }

  const tokenEnterpriseId = getEnterpriseIdFromToken()
  const effectiveEnterpriseId = enterpriseId || tokenEnterpriseId

  const response = await fetch(`${NOC_DISPLAY_BASE_URL}/agency-server/5KL68-PO5J6/delete-fingerprint`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-admin-user': user?.profile.sub || user?.profile.preferred_username || '',
      ...(effectiveEnterpriseId && {
        'X-Placebet-Enterprise': effectiveEnterpriseId
      }),
      'x-license': licenseKey,
      'x-server': serverId,
    },
    body: JSON.stringify({}),
  })

  let responseData: any
  try {
    responseData = await response.json()
  } catch {
    throw new AssignmentError(
      `No se pudo procesar la respuesta del servidor: ${response.status} ${response.statusText}`,
      response.ok ? 'UNKNOWN' : `${response.status}`,
    )
  }

  if (!response.ok) {
    throw new AssignmentError(
      responseData?.detail || `Error al desasociar el servidor (${response.status})`,
      `${response.status}`,
    )
  }

  return {
    success: true,
    detail: responseData?.detail || 'El servidor fué reiniciado de manera exitosa.',
  }
}

